if (!customElements.get("quick-view-drawer")) {
  customElements.define(
    "quick-view-drawer",
    class QuickViewDrawer extends DrawerModal {
      constructor() {
        super();
        this.fetchedUrls = {};
        this.cssAssets = [
          "section-main-product.css",
          "component-price.css",
          "component-tooltip.css",
          "component-deferred-media.css",
        ];
        this.jsAssets = ["media-gallery.js", "recipient-form.js"];
        this.selector = "quickview-drawer";
      }

      connectedCallback() {
        super.connectedCallback?.();
        this.drawerContent = this.querySelector(".drawer-content-inner");
        this.drawerFooter = this.querySelector(".drawer-footer");
      }

      open(opener) {
        super.open(opener);
        const card = opener.closest(".card-product, .product-row");
        const jsProductLink = card.querySelector(".js-product-link");
        this.productUrl = jsProductLink.getAttribute("href");
        this.loadProduct(this.productUrl);
      }

      close() {
        super.close();
        this.drawerContent.innerHTML = '<span class="spinner spinner-lg"></span>';
        this.drawerFooter.innerHTML = "";
      }

      async loadProduct(productUrl) {
        if (this.fetchedUrls[productUrl]) {
          const htmlClone = this.fetchedUrls[productUrl].cloneNode(true);
          this.renderProduct(htmlClone);
          return;
        }

        try {
          const response = await fetch(productUrl);
          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, "text/html");

          this.fetchedUrls[productUrl] = html;
          this.loadAssets(html);
          const htmlClone = html.cloneNode(true);
          this.renderProduct(htmlClone);
        } catch (error) {
          console.error(error);
        }
      }

      renderProduct(html, variant) {
        const productPage = html.querySelector('[id^="ProductPage-template--"][id*="__main"]');

        if (!productPage) {
          console.warn("QuickViewDrawer: product page node not found in fetched HTML.");
          return;
        }

        const sectionId = productPage.dataset.sectionId;
        this.realSectionId = sectionId;

        productPage.innerHTML = productPage.innerHTML.replaceAll(sectionId, this.selector);

        if (this.querySelector("product-form") && variant) {
          this.handleUpdateProduct(productPage, sectionId, variant);
          return;
        }

        this.drawerContent.innerHTML = this.getQuickviewContentHtml(productPage, sectionId);
        this.drawerFooter.innerHTML = this.getProductFormHtml(productPage);

        this.querySelectorAll(".aiden-selection-banner").forEach((el) => el.remove());

        this.fixVariantPickerSectionIds(sectionId);

        const productSKU = this.querySelector("#ProductSku-quickview-drawer")?.textContent;
        if (productSKU) {
          productInventoryApi(productSKU);
        }

        if (typeof window.initHowToGetIt === "function") {
          window.initHowToGetIt(this);
        }
      }

      handleUpdateProduct(html, sectionId, variant) {
        this.drawerContent.innerHTML = this.getQuickviewContentHtml(html, sectionId);
        this.updateProductPrice(html);
        this.updateProductForm(html);
        this.updateVariantInput(variant);

        this.querySelectorAll(".aiden-selection-banner").forEach((el) => el.remove());

        this.fixVariantPickerSectionIds(sectionId);

        if (typeof window.initHowToGetIt === "function") {
          window.initHowToGetIt(this);
        }
      }

      fixVariantPickerSectionIds(sectionId) {
        if (!sectionId) return;
        const pickers = this.querySelectorAll("variant-selects, variant-radios");
        pickers.forEach((picker) => {
          picker.setAttribute("data-section", sectionId);
          picker.setAttribute("data-update-url", "false");
          picker.sectionId = sectionId;
        });
      }

      updateProductPrice(html) {
        const priceDestination = this.querySelector(".drawer-footer .product-block-price");

        if (priceDestination) {
          priceDestination.outerHTML = this.getProductPriceHtml(html);
        }
      }

      updateProductForm(html) {
        const productFormSource = html
          .querySelector(`#product-form-${this.selector}`)
          ?.closest("product-form");
        const productFormDestination = document
          .querySelector(`#product-form-${this.selector}`)
          ?.closest("product-form");

        if (!productFormSource) return;

        const button = html.querySelector(".sticky-addtocart-panel-right > button");
        productFormSource.querySelector(".sticky-addtocart-panel")?.remove();
        if (button) {
          productFormSource.querySelector(".product-actions-add-to-cart")?.append(button);
        }

        productFormSource.querySelector(".bulk-pricing-wrapper")?.remove();
        productFormSource.querySelector(".how-get-it")?.remove();
        productFormSource.querySelector(".aiden-selection-banner")?.remove();

        if (productFormDestination) {
          productFormDestination.outerHTML = productFormSource.outerHTML;

          const newProductForm = document
            .querySelector(`#product-form-${this.selector}`)
            ?.closest("product-form");
          if (newProductForm) {
            newProductForm.style.opacity = "1";
          }
        }
      }

      updateVariantInput(variant) {
        const productForms = this.querySelectorAll(
          `#product-form-${this.selector}, #product-form-installment`,
        );

        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variant.id;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      getQuickviewContentHtml(html, sectionId) {
        return `
          <div class="product-quick-view-header">
            ${this.getProductMediaGalleryHtml(html)}
            ${this.getProductInfoHtml(html)}
          </div>
          ${this.getProductVariantsHtml(html, sectionId)}
          ${this.getProductBulkPriceOptionsHtml(html)}
          ${this.getProductHowGetItHtml(html)}
          ${this.getBulkPricingDrawerHtml(html)}
          ${this.getProductBackorderHtml(html)}
        `;
      }

      getProductMediaGalleryHtml(html) {
        const gallery = html.querySelector(".product-media-gallery-wrap");
        if (!gallery) return "";

        gallery.querySelector(".product-media-gallery-message")?.remove();
        gallery.querySelector(".product-media-scroll-wrap")?.remove();
        gallery.querySelector("carousel-navigation")?.remove();
        gallery.querySelector("product-modal")?.remove();
        gallery.querySelectorAll(".deferred-media-poster-button").forEach((button) => button.remove());
        gallery.querySelectorAll(".product-xr-button").forEach((button) => button.remove());

        gallery.querySelectorAll("img").forEach((img) => {
          try {
            const width = 100;
            const src = img.getAttribute("src").split("&width=")[0];
            const aspectRatio = img.width / img.height;
            const newSrc1x = `${src}&width=${width}`;
            const newSrc2x = `${src}&width=${width * 2}`;
            img.setAttribute("src", newSrc1x);
            img.setAttribute("srcset", `${newSrc1x} 1x, ${newSrc2x} 2x`);
            img.removeAttribute("sizes");
            img.setAttribute("width", width * 2);
            img.setAttribute("height", Math.floor((width * 2) / aspectRatio));
          } catch (error) {
            console.error(error);
          }
        });

        return gallery.outerHTML;
      }

      getProductInfoHtml(html) {
        const productInfo = html.querySelector(".product-info-main");
        if (!productInfo) return "";
        productInfo.querySelector(".product-info-meta-item.last")?.remove();
        productInfo.querySelector(".product-info-badges")?.remove();

        if (!productInfo.querySelector(".product-info-view-more")) {
          const fullDetailsButton = `
            <div class="product-info-view-more">
              <a href="${this.productUrl}" class="button-text-link">
                ${window.productsStrings.viewFullDetails}
                <span class="angle angle-right">
                  <span class="angle-icon"></span>
                </span>
              </a>
            </div>
          `;
          productInfo.insertAdjacentHTML("beforeend", fullDetailsButton);
        }

        return productInfo.outerHTML;
      }

      getProductVariantsHtml(html, sectionId) {
        const variants = html.querySelector(".product-variants");
        const variantsElement =
          variants?.querySelector("variant-radios") ||
          variants?.querySelector("variant-selects") ||
          null;
        if (variantsElement) {
          variantsElement.setAttribute("data-update-url", "false");
          variantsElement.setAttribute("data-section", sectionId);
        }
        return variants ? variants.outerHTML : "";
      }

      getProductPriceHtml(html) {
        const productPrice = html.querySelector(".product-block-price");
        if (productPrice) {
          const priceWrapper = productPrice.querySelector(".price");
          if (priceWrapper && !productPrice.querySelector(".qv-price")) {
            priceWrapper.insertAdjacentHTML(
              "beforebegin",
              `<div class="qv-price-wrapper">
                <div class="qv-price">
                  ${window.productsStrings.quickViewPrice}
                  ${
                    window.productsStrings.priceTaxSuffix
                      ? `<span class="sup">(${window.productsStrings.priceTaxSuffix})</span>`
                      : ""
                  }
                </div>
              </div>`
            );
          }
          return productPrice.outerHTML;
        }
        return "";
      }

      getProductBulkPriceOptionsHtml(html) {
        const productBulkPriceOptions = html.querySelector(".product-block-bulk-price-options");
        return productBulkPriceOptions ? productBulkPriceOptions.outerHTML : "";
      }

      getProductHowGetItHtml(html) {
        const productHowGetIt = html.querySelector(".product-block-how-get-it");
        return productHowGetIt ? productHowGetIt.outerHTML : "";
      }

      getBulkPricingDrawerHtml(html) {
        const drawer = html.querySelector(".product-block-bulk-price-drawer");
        if (!drawer) return "";

        const header = drawer.querySelector(".bulk-drawer-header");
        if (header && !header.querySelector(".bulk-drawer-back")) {
          header.insertAdjacentHTML(
            "afterbegin",
            `
            <button type="button" class="bulk-drawer-back" aria-label="Back">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 4L6.5 10L12.5 16" stroke="#000933" stroke-width="2"/>
              </svg>
            </button>
            `
          );
        }
        return drawer.outerHTML;
      }

      /*
      Aiden banner removed from Quick View.

      getProductSelectionAidHtml(html) {
        const selectionAid = html.querySelector(".aiden-selection-banner");
        return selectionAid ? selectionAid.outerHTML : "";
      }
      */

      getProductBackorderHtml(html) {
        const backorderWrapper = html.querySelector(".product-backorder-wrapper");
        return backorderWrapper ? backorderWrapper.outerHTML : "";
      }

      getProductFormHtml(html) {
        const productForm = html.querySelector(".product-block-product-form");
        if (productForm) {
          productForm.querySelector(".bulk-pricing-wrapper")?.remove();
          productForm.querySelector(".how-get-it")?.remove();
          productForm.querySelector(".aiden-selection-banner")?.remove();

          const button = html.querySelector(".sticky-addtocart-panel-right > button");
          productForm.querySelector(".sticky-addtocart-panel")?.remove();
          if (button) {
            productForm.querySelector(".product-actions-add-to-cart")?.append(button);
          }
        }

        const form = productForm?.querySelector("form");
        if (form) {
          form.setAttribute("data-event-context", "QuickViewDrawer");
        }

        if (productForm) {
          return `
            ${this.getProductPriceHtml(html)}
            ${productForm.outerHTML}
          `;
        }
        return "";
      }

      loadAssets(html) {
        this.cssAssets.forEach((asset) => {
          const links = html.querySelectorAll(`link[href*="${asset}"]`);
          links.forEach((link) => {
            const href = link.getAttribute("href");
            if (!document.querySelector(`link[href="${href}"]`)) {
              const newLink = document.createElement("link");
              newLink.href = href;
              newLink.rel = link.getAttribute("rel") || "stylesheet";
              newLink.type = link.getAttribute("type");
              newLink.media = link.getAttribute("media");
              document.head.appendChild(newLink);
            }
          });
        });

        this.jsAssets.forEach((asset) => {
          const scripts = html.querySelectorAll(`script[src*="${asset}"]`);
          scripts.forEach((script) => {
            const src = script.getAttribute("src");
            if (!document.querySelector(`script[src="${src}"]`)) {
              const newScript = document.createElement("script");
              newScript.src = src;
              if (script.hasAttribute("defer")) {
                newScript.defer = true;
              }
              document.head.appendChild(newScript);
            }
          });
        });
      }
    },
  );
}